import { useEffect, useRef, useState } from "react";
import type { RoomView } from "../../shared/game-types.js";
import { useCountdown } from "../lib/socket.js";
import { useTheme } from "../lib/tuning.js";

/** What the clock currently on screen is counting down to. */
const LABELS: Partial<Record<RoomView["phase"], string>> = {
  "category-ban": "Bans close",
  "category-intro": "Board opens",
  playing: "Turn ends",
  "category-reveal": "Next category",
};

/**
 * A drain bar pinned under everything, so the room always knows how long until
 * the screen changes.
 *
 * The duration of a phase is never sent — the reveal in particular runs for as
 * long as the stagger needs — so rather than duplicating that arithmetic on the
 * client, the bar remembers the largest remaining time it has seen this phase
 * and uses it as the denominator. Attaching mid-phase just means the bar starts
 * full, which is the honest thing to show anyway.
 */
export function usePhaseProgress(room: RoomView): {
  remaining: number;
  fraction: number;
  label: string | undefined;
} {
  const remaining = useCountdown(room.deadline);
  const [total, setTotal] = useState(0);

  // Phase alone is not a unique key: two categories in a row both pass through
  // 'category-intro' and the second must not inherit the first's scale.
  const phaseKey = `${room.phase}:${room.category?.index ?? -1}`;
  const seenKey = useRef("");

  useEffect(() => {
    if (seenKey.current !== phaseKey) {
      seenKey.current = phaseKey;
      setTotal(0);
    }
  }, [phaseKey]);

  useEffect(() => {
    setTotal((t) => (remaining > t ? remaining : t));
  }, [remaining]);

  return {
    remaining,
    fraction: total > 0 ? Math.max(0, Math.min(1, remaining / total)) : 0,
    label: LABELS[room.phase],
  };
}

export function NextUpBar({ room }: { room: RoomView }) {
  const theme = useTheme();
  const { remaining, fraction, label } = usePhaseProgress(room);
  if (!label || room.deadline === null) return null;
  if (room.phase === "playing" && !theme.nextUp.showDuringPlay) return null;

  const urgent = fraction <= theme.nextUp.urgentBelowFraction;

  return (
    <div className="nextUp" data-urgent={urgent}>
      <div className="nextUp__label">
        <span>{label}</span>
        <span className="numeral">{Math.ceil(remaining)}s</span>
      </div>
      <div className="nextUp__track">
        <div
          className="nextUp__fill"
          style={{ width: `${fraction * 100}%` }}
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(fraction * 100)}
          aria-label={label}
        />
      </div>
    </div>
  );
}
